import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { omit } from 'lodash';
import numeral from 'numeral';
import styled from 'styled-components';

import { TextInput, Button, ErrorMessage, useScreenQuery } from 'playup-web-core';

import FileInput from '../../../components/FileInput';
import useNotify from '../../../hooks/useNotify';
import { request2faCode, createBankAccount } from '../../../api/withdraw';
import { parseApiError } from '../../../utils/parseApiError';
import TwoFAModal from './TwoFAModal';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';

interface Props {
	documentRequired?: boolean;
	onWithdrawalCreated: () => void;
}

interface FormData {
	account_name: string;
	bank_id: string | null;
	account_number: string;
	document: string | ArrayBuffer | null;
	swift_code: string | null;
	bank_name: string | null;
}

// TODO: The name of this component is pretty bad.
const NoBankAccount: React.FC<Props> = (props) => {
	const { isSmall } = useScreenQuery();
	const { register, handleSubmit, setValue, reset, watch } = useForm<FormData>({});
	const [document, setDocument] = React.useState<FileReader['result']>(null);
	const [sending, setSending] = React.useState<boolean>(false);
	const [errors, setErrors] = React.useState<APIError>();
	const [show2FAModal, setShow2FAModal] = React.useState<boolean>(false);
	const { user } = React.useContext(PortalSessionContext);

	// This will be used to determine whether the user's Address is in Australia or not.
	const isForeign =
		user?.country?.toLowerCase() !== 'aus' &&
		user?.country?.toLowerCase() !== 'australia' &&
		user?.country?.toLowerCase() !== 'au';

	const onSubmit: SubmitHandler<FormData> = (data) => {
		setErrors({});

		request2faCode(data)
			.then(() => {
				setSending(false);
				setShow2FAModal(true);
			})
			.catch((error) => {
				setErrors(error.errors);
				setSending(false);
			});
	};

	const handleUpload = (content: FileReader['result']) => setDocument(content);

	const { account_name, account_number, bank_id, swift_code, bank_name } = watch();

	const submitDisabled = [account_name, account_number].some((value) => !value);

	return (
		<>
			<form onSubmit={handleSubmit(onSubmit)}>
				<StyledTextInput
					isSmallScreen={isSmall}
					kind="white"
					label="Account Name"
					type="text"
					placeholder="John Citizen"
					error={parseApiError(errors, 'account_name')}
					{...register('account_name', { required: true })}
				/>
				{isForeign ? (
					<>
						<StyledTextInput
							isSmallScreen={isSmall}
							kind="white"
							label="Bank Name"
							type="text"
							placeholder="Bank Name"
							error={parseApiError(errors, 'bank_name')}
							{...register('bank_name')}
						/>
						<StyledTextInput
							isSmallScreen={isSmall}
							kind="white"
							label="Swift Code"
							type="text"
							placeholder="Swift Code"
							error={parseApiError(errors, 'swift_code')}
							{...register('swift_code')}
						/>
					</>
				) : (
					<StyledTextInput
						isSmallScreen={isSmall}
						kind="white"
						label="BSB"
						type="text"
						placeholder="123456"
						error={parseApiError(errors, 'bank_id')}
						{...register('bank_id')}
					/>
				)}
				<StyledTextInput
					isSmallScreen={isSmall}
					kind="white"
					label="Account Number"
					type="text"
					placeholder="12345678"
					error={parseApiError(errors, 'account_number')}
					{...register('account_number', { required: true })}
				/>
				{props.documentRequired && (
					<>
						{/*TODO: no design provided for this section*/}
						<FieldText isSmallScreen={isSmall}>
							UPLOAD A CLOSE UP OF YOUR BANK STATEMENT WHICH SHOWS FULL NAME, ADDRESS, BSB AND ACCOUNT NUMBER. ALL
							INFORMATION MUST BE DISPLAYED WITHIN THE ONE SCREENSHOT OR DOCUMENT UPLOADED.
						</FieldText>
						<FileInput
							className="_w-full"
							label="Bank Statement"
							accept=".pdf,.png,.jpg,.jpeg"
							onUpload={handleUpload}
							error={parseApiError(errors, 'document')}
							{...register('document', { required: true })}
						/>
						<FieldText isSmallScreen={isSmall}>Accepted files: (jpg, png, pdf)</FieldText>
						{errors?.document && <ErrorMessage>{parseApiError(errors, 'document')}</ErrorMessage>}
					</>
				)}
				<StyledButton isSmallScreen={isSmall} kind="white" type="submit" disabled={submitDisabled} isLoading={sending}>
					Continue
				</StyledButton>
				{errors?.error && <ErrorMessage>{parseApiError(errors)}</ErrorMessage>}
			</form>
			{show2FAModal && (
				<TwoFAModal
					onClose={() => setShow2FAModal(false)}
					bankData={{ account_name, bank_id, swift_code, bank_name, account_number }}
					onWithdrawlCreated={props.onWithdrawalCreated}
				/>
			)}
		</>
	);
};

export default NoBankAccount;

const StyledTextInput = styled(TextInput)<{ isSmallScreen: boolean }>`
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '20px' : '30px')};
`;

const StyledButton = styled(Button)<{ isSmallScreen: boolean }>`
	margin-top: ${({ isSmallScreen }) => (isSmallScreen ? '30px' : '40px')};
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '10px' : '20px')};
`;

const FieldText = styled.div<{ isSmallScreen: boolean }>`
	color: ${({ theme }) => theme.colors.darkR};
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '14px' : '18px')};
	margin: ${({ isSmallScreen }) => (isSmallScreen ? '5px 0' : '10px 0')};
`;
